import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { Modal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';
import { PhoneField } from '@/components/forms';
import { ApiError, isForceLogoutError } from '@/lib/api/errors';
import { mutationErrorMessage, mutationErrorTone } from '@/lib/api/mutation-toast';

import { EDIT_PROFILE_COPY, PHONE_CHANGE_COPY, PROFILE_ERROR_CODES } from '../../constants';
import { useSendNewPhoneOtp } from '../../hooks';
import type { CodeDispatch } from '../../schemas';

const PHONE_LENGTH = 10;

export type NewPhoneStepProps = {
  /** The Registered phone number, any format; compared as its last 10 digits. */
  currentPhone: string;
  /** Re-entry from a verify-new 409: the number tried, and the error to show under it. */
  initial?: { phone: string; error: string };
  onSent: (dispatch: CodeDispatch, phone: string) => void;
  onExpired: () => void;
  onClose: () => void;
};

/** Phone change Step 3 (US-117543 §F, grilling Q16; Figma 1790:981080 / 983174 / 981778 / 982476). */
export function NewPhoneStep({
  currentPhone,
  initial,
  onSent,
  onExpired,
  onClose,
}: NewPhoneStepProps) {
  const toast = useToast();
  const send = useSendNewPhoneOtp();
  const current = currentPhone.replace(/\D/g, '').slice(-PHONE_LENGTH);
  const schema = useMemo(
    () =>
      z.object({
        phone: z
          .string()
          .length(PHONE_LENGTH, PHONE_CHANGE_COPY.INVALID)
          .refine((v) => v !== current, PHONE_CHANGE_COPY.SAME_AS_CURRENT),
      }),
    [current],
  );
  const { control, handleSubmit, setError, setValue, formState } = useForm({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: { phone: '' },
  });
  const phone = useWatch({ control, name: 'phone' });
  // Read up front: RHF subscribes to a formState field only once it is read, and a short-circuited
  // `phone.length < 10 || errors.phone` would miss the seeded error below until the next render.
  const hasError = Boolean(formState.errors.phone);

  // Touched, so `onTouched` re-validates on the next edit and the server error clears then.
  useEffect(() => {
    if (!initial) return;
    setValue('phone', initial.phone, { shouldTouch: true });
    setError('phone', { type: 'server', message: initial.error });
  }, [initial, setValue, setError]);

  const inline = (message: string) => setError('phone', { type: 'server', message });

  const onSubmit = handleSubmit(async (values) => {
    try {
      onSent(await send.mutateAsync(values.phone), values.phone);
    } catch (error) {
      if (isForceLogoutError(error)) return; // the force-logout seam already toasts
      if (error instanceof ApiError) {
        if (error.code === PROFILE_ERROR_CODES.PHONE_VERIFY_REQUIRED) return onExpired();
        if (error.code === PROFILE_ERROR_CODES.PHONE_ALREADY_EXISTS) {
          return inline(PHONE_CHANGE_COPY.TAKEN);
        }
        if (
          error.code === PROFILE_ERROR_CODES.PHONE_SAME_AS_CURRENT ||
          error.code === PROFILE_ERROR_CODES.OTP_PHONE_UNDELIVERABLE
        ) {
          return inline(error.message);
        }
        if (error.status === 400) return inline(PHONE_CHANGE_COPY.INVALID);
      }
      // 503, or no response at all: toast and stay.
      toast[mutationErrorTone(error)]({ title: mutationErrorMessage(error) });
    }
  });

  return (
    <Modal
      open
      size="sm"
      onClose={onClose}
      title={PHONE_CHANGE_COPY.STEP3_TITLE}
      subtitle={PHONE_CHANGE_COPY.STEP3_SUBTITLE}
      primaryText="Send Code"
      onPrimary={() => void onSubmit()}
      primaryDisabled={hasError || phone.length < PHONE_LENGTH}
      loading={formState.isSubmitting}
    >
      <PhoneField
        name="phone"
        control={control}
        label={EDIT_PROFILE_COPY.PHONE}
        placeholder={PHONE_CHANGE_COPY.PLACEHOLDER}
        required
      />
    </Modal>
  );
}
